import { z } from 'zod';

import { ToolOutputSuccessSchema } from '#plugin-chat/lib/tool/output-types.ts';

const SLIDE_TYPES = ['option', 'checkbox', 'confirmation'] as const;
export type SlideType = (typeof SLIDE_TYPES)[number];

/** Option within a slide - represents a selectable choice. */
const slideOptionSchema = z.object({
  id: z.string().describe('Unique identifier for this option (used in response)'),
  label: z.string().describe('Display text for the option (1-3 words)'),
  description: z
    .string()
    .optional()
    .describe('Longer explanation shown below label, include unless label is self-explanatory'),
});

export type SlideOption = z.infer<typeof slideOptionSchema>;

/**
 * Single slide in the stepper flow.
 *
 * Slide type determines rendering behavior:
 * - `option`: Radio-style selection (exactly one choice)
 * - `checkbox`: Multi-select with chips (one or more choices)
 * - `confirmation`: Yes/No with optional items preview
 */
const stepperSlideSchema = z.object({
  title: z.string().describe('Short slide header (2-5 words)'),
  type: z
    .enum(SLIDE_TYPES)
    .describe(
      'Slide interaction type: ' +
        '"option" = select exactly one (radio-style), ' +
        '"checkbox" = select one or more (chips), ' +
        '"confirmation" = yes/no with optional items list',
    ),
  question: z
    .string()
    .describe(
      'Question or instruction text displayed to user. Should be self-contained: include up to one paragraph of relevant context.',
    ),
  options: z
    .array(slideOptionSchema)
    .min(2)
    .describe(
      'Available choices (2+ options). ' +
        'For confirmation type, use exactly 2 options: one for confirm (e.g., "Yes, proceed"), one for cancel (e.g., "Cancel").',
    ),
});

export type StepperSlide = z.infer<typeof stepperSlideSchema>;

/** Response from a single slide - the selected option ID(s). */
export interface SlideResponse {
  /** Selected option IDs (single for option/button/confirmation, multiple for checkbox) */
  selectedIds: string[];
}

/** Complete response from the stepper flow. */
export interface StepperResponse {
  /** Responses for each slide in order */
  slideResponses: SlideResponse[];
  /** Whether the user completed the flow (true) or cancelled (false) */
  completed: boolean;
}

/** Payload structure for stepper slides */
export interface StepperPayload {
  type: 'stepper';
  slideIndex: number;
  totalSlides: number;
  slide: StepperSlide;
}

export const AskUserQuestionOutputSchema = ToolOutputSuccessSchema.extend({
  /** User selections keyed by slide title. */
  data: z.record(z.string(), z.array(z.string())),
});

/** @public */
export type AskUserQuestionOutput = z.infer<typeof AskUserQuestionOutputSchema>;

export const AskUserQuestionInputSchema = z.object({
  slides: z
    .array(stepperSlideSchema)
    .min(1)
    .max(5)
    .describe(
      'Stepper slides (1-5). ' +
        'Use 1 slide for quick questions, confirmations, or simple choices. ' +
        'Use 2-5 slides for guided flows requiring multiple steps. ' +
        'Each slide is displayed one at a time with back navigation.',
    ),
});

export type AskUserQuestionInput = z.infer<typeof AskUserQuestionInputSchema>;
